import { Button, InlineMessage } from "@saviynt/design-system";
import { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useTestConnection } from "../hooks/useTestConnection";
import type { SsoTestResult } from "../hooks/useTestConnection";

interface TestConnectionButtonProps {
  isVisible: boolean;
  onTestResult: (result: SsoTestResult) => void;
}

export default function TestConnectionButton({
  isVisible,
  onTestResult,
}: TestConnectionButtonProps) {
  const intl = useIntl();
  const { testConnection, isLoading, error, testResult, isBroadcastSupported } =
    useTestConnection();

  useEffect(() => {
    if (testResult) {
      onTestResult(testResult);
    }
  }, [testResult, onTestResult]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="SsoCreate__section">
      <div style={{ width: "fit-content" }}>
        <Button
          kind="outlined"
          size="medium"
          onClick={testConnection}
          isLoading={isLoading}
          aria-label={intl.formatMessage({
            id: "kPxo4P",
            defaultMessage: "Test Connection",
            description: "Aria label for test connection button",
          })}
        >
          <FormattedMessage
            id="CWZohu"
            defaultMessage="Test Connection"
            description="Button label to test SSO connection"
          />
        </Button>
      </div>

      <div aria-live="polite">
        {error && (
          <InlineMessage colorTheme="critical" text={error} size="small" />
        )}

        {!isBroadcastSupported &&
          !error &&
          !testResult &&
          isLoading === false && (
            <InlineMessage
              colorTheme="info"
              text={intl.formatMessage({
                id: "ty5N51",
                defaultMessage:
                  "Your browser does not support automatic test result detection. Please check the test tab for results.",
                description:
                  "Informational message when BroadcastChannel is not supported",
              })}
              size="small"
            />
          )}

        {testResult && testResult.success && (
          <InlineMessage
            colorTheme="success"
            text={intl.formatMessage({
              id: "pMM/6Z",
              defaultMessage:
                "SSO test connection successful. You can now enable SSO for all users.",
              description: "Success message after SSO test connection",
            })}
            size="small"
          />
        )}

        {testResult && !testResult.success && (
          <InlineMessage
            colorTheme="critical"
            text={
              testResult.message ||
              intl.formatMessage({
                id: "4gcceY",
                defaultMessage:
                  "SSO test connection failed. Please check your IdP configuration.",
                description: "Failure message after SSO test connection",
              })
            }
            size="small"
          />
        )}
      </div>
    </div>
  );
}
